import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import type { PurchaseOrderView } from '@wodi/shared';
import { Button, DateField, LoadingBlock, Notice, PageHeader, TextArea, TextField } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { createSupplierInvoice, purchaseOrderQuery } from '../api/procurement.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen } from '../layout/screen.tsx';
import { LoadFailed, OfflineNote } from '../procurement/common.tsx';
import { centsToText, todayEat } from '../procurement/format.ts';
import { readPrice } from '../procurement/pack-lines.tsx';
import { errorText, newRequestKey } from '../stock/common.tsx';
import { formatCents, formatInPacks, readQuantity } from '../stock/format.ts';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

// Record a supplier invoice against its LPO (docs/plans/phase2-procurement.md §5.10). The
// three-way match is worked out by the server and shown on the invoice once recorded.

const route = getRouteApi('/_app/procurement/invoices/new');

export function NewSupplierInvoicePage() {
  const { orderId } = route.useSearch();
  const order = useQuery(purchaseOrderQuery(orderId));
  if (order.isPending) return <LoadingBlock label="Loading the LPO" rows={4} />;
  if (order.isError)
    return (
      <LoadFailed
        error={order.error}
        fallback="The LPO couldn't be loaded."
        onRetry={() => void order.refetch()}
      />
    );
  return <InvoiceForm order={order.data} />;
}

interface Line {
  orderLineId: string;
  packsText: string;
  priceText: string;
}

function InvoiceForm({ order }: { order: PurchaseOrderView }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reference, setReference] = useState('');
  const [invoiceDate, setInvoiceDate] = useState<string | null>(todayEat());
  const [dueOn, setDueOn] = useState<string | null>(null);
  const [vatText, setVatText] = useState('0');
  const [totalText, setTotalText] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<Line[]>(() =>
    order.lines.map((l) => ({
      orderLineId: l.id,
      packsText: '',
      priceText: centsToText(l.packPrice.cents),
    })),
  );
  const [tried, setTried] = useState(false);
  const [requestKey] = useState(newRequestKey);

  const used = lines.filter((l) => l.packsText.trim() !== '');
  const linesTotal = used.reduce((sum, l) => {
    const packs = readQuantity(l.packsText);
    const price = readPrice(l.priceText);
    return packs === null || price === null ? sum : sum + price * BigInt(packs);
  }, 0n);
  const vat = readPrice(vatText);
  const total = readPrice(totalText);
  const lineOk = (l: Line) =>
    (readQuantity(l.packsText) ?? 0) > 0 && readPrice(l.priceText) !== null;
  const valid =
    reference.trim() !== '' &&
    invoiceDate !== null &&
    used.length > 0 &&
    used.every(lineOk) &&
    vat !== null &&
    total !== null;

  const record = useMutation({
    mutationFn: () =>
      createSupplierInvoice({
        clientRequestId: requestKey,
        purchaseOrderId: order.id,
        supplierReference: reference.trim(),
        invoiceDate: invoiceDate ?? '',
        ...(dueOn === null ? {} : { dueOn }),
        vat: { currency: 'KES', cents: (vat ?? 0n).toString() },
        total: { currency: 'KES', cents: (total ?? 0n).toString() },
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        lines: used.map((l) => ({
          purchaseOrderLineId: l.orderLineId,
          packs: readQuantity(l.packsText) ?? 0,
          packPrice: { currency: 'KES' as const, cents: (readPrice(l.priceText) ?? 0n).toString() },
        })),
      }),
    onSuccess: async (invoice) => {
      await queryClient.invalidateQueries({ queryKey: ['procurement'] });
      toasts.show({ title: 'Invoice recorded', description: invoice.number });
      void navigate({ to: '/procurement/invoices/$invoiceId', params: { invoiceId: invoice.id } });
    },
  });
  const duplicateId =
    record.error instanceof ApiError && record.error.code === 'supplier_invoice_duplicate'
      ? record.error.context?.['invoiceId']
      : undefined;

  return (
    <div className={styles.page}>
      <p className={styles.meta}>
        <Link to="/procurement/orders/$orderId" params={{ orderId: order.id }}>
          Back to {order.number}
        </Link>
      </p>
      <PageHeader title={`Record supplier invoice for ${order.number}`} />
      <form
        className={`${styles.wideForm} ${screen.panel}`}
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (valid) record.mutate();
        }}
      >
        <p className={styles.meta}>
          From {order.supplier.name}. Enter the invoice exactly as printed; Wodi compares it with
          the LPO and what was received.
        </p>
        <div className={styles.row}>
          <TextField
            label="Supplier's invoice number"
            value={reference}
            onChange={setReference}
            {...(tried && reference.trim() === ''
              ? { errorMessage: 'Enter the invoice number as printed.' }
              : {})}
          />
          <DateField label="Invoice date" value={invoiceDate} onChange={setInvoiceDate} />
          <DateField label="Due on (optional)" value={dueOn} onChange={setDueOn} />
        </div>
        {order.lines.map((ol, index) => {
          const line = lines[index];
          if (line === undefined) return null;
          const set = (patch: Partial<Line>) => {
            setLines((all) => all.map((l, i) => (i === index ? { ...l, ...patch } : l)));
          };
          const bad = tried && line.packsText.trim() !== '' && !lineOk(line);
          return (
            <fieldset key={ol.id} className={styles.line}>
              <legend className={styles.lineHeading}>
                Line {ol.lineNo}: {ol.itemName}
              </legend>
              <p className={styles.meta}>
                Ordered{' '}
                {formatInPacks(ol.quantity, ol.baseUnitDisplay, {
                  display: ol.packUnitDisplay,
                  baseUnitsPerPack: ol.baseUnitsPerPack,
                })}{' '}
                at {formatCents(ol.packPrice.cents)} per {ol.packUnitDisplay}; received{' '}
                {ol.received} {ol.baseUnitDisplay}. Leave packs empty if this invoice doesn't
                include the item.
              </p>
              <div className={styles.row}>
                <TextField
                  label={`Packs invoiced (${ol.packUnitDisplay})`}
                  inputMode="numeric"
                  value={line.packsText}
                  onChange={(v) => {
                    set({ packsText: v });
                  }}
                />
                <TextField
                  label="Price per pack on the invoice"
                  unit="KES"
                  inputMode="decimal"
                  value={line.priceText}
                  onChange={(v) => {
                    set({ priceText: v });
                  }}
                />
              </div>
              {bad ? <Notice tone="error">Enter whole packs and a price like 1,200.</Notice> : null}
            </fieldset>
          );
        })}
        <div className={styles.row}>
          <TextField
            label="VAT as printed"
            unit="KES"
            inputMode="decimal"
            value={vatText}
            onChange={setVatText}
          />
          <TextField
            label="Invoice total as printed"
            unit="KES"
            inputMode="decimal"
            value={totalText}
            onChange={setTotalText}
            {...(tried && total === null ? { errorMessage: 'Enter the total, like 5,200.' } : {})}
          />
        </div>
        <p className={styles.total}>Lines add up to {formatCents(linesTotal.toString())}</p>
        {tried && used.length === 0 ? (
          <Notice tone="error">Enter the packs invoiced on at least one line.</Notice>
        ) : null}
        <TextArea label="Note (optional)" value={note} onChange={setNote} />
        {record.error === null ? null : (
          <Notice tone="error">
            {errorText(record.error, "The invoice wasn't recorded. Try again.")}
            {typeof duplicateId === 'string' ? (
              <>
                {' '}
                <Link to="/procurement/invoices/$invoiceId" params={{ invoiceId: duplicateId }}>
                  Open the recorded invoice
                </Link>
              </>
            ) : null}
          </Notice>
        )}
        <OfflineNote what="Recording an invoice" />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={record.isPending}
            pendingLabel="Recording…"
          >
            Record invoice
          </Button>
        </div>
      </form>
    </div>
  );
}
